// The credit bureau report grid on a loan application (Microlending Act
// s 24(4)): blank rows, what is sent to the API, and the totals shown under
// the grid. Pure, so it is tested on its own (bureauView.test.js);
// BureauReportSection.jsx lays it out.

export const ACCOUNT_TYPES = ['Microloan', 'Personal loan', 'Home loan', 'Vehicle finance', 'Credit card', 'Overdraft', 'Retail account', 'Other'];
export const ACCOUNT_STATUSES = ['Active', 'In arrears', 'Default', 'Judgment', 'Written off', 'Closed', 'Settled'];
// Who the report (and each account) is from: Koniza's own bureau check, or
// the client's report typed in until confirmed against Koniza's own.
export const SOURCES = [
  { value: 'Bureau', label: 'Koniza bureau check' },
  { value: 'ClientSupplied', label: 'Client-supplied report' },
];
// Not owed (the check leaves them out) and adverse (the check declines).
const NOT_OWED = ['Closed', 'Settled'];
const ADVERSE = ['Default', 'Judgment', 'Written off'];

/** An empty grid row, dated as the report and from the same source. */
export const blankAccount = (reportDate = '', source = 'Bureau') => ({
  creditor: '', account_type: 'Personal loan', instalment: '', balance: '', status: 'Active', report_date: reportDate || '', source,
});

/** The form as the API saves it (PUT /api/loans/:id/bureau-report). */
export const toPayload = (form) => ({
  report_date: form?.report_date || '',
  bureau_name: String(form?.bureau_name || '').trim(),
  source: form?.source || 'Bureau',
  accounts: (Array.isArray(form?.accounts) ? form.accounts : [])
    .filter((a) => String(a.creditor || '').trim() || String(a.instalment ?? '').trim() || String(a.balance ?? '').trim())
    .map((a) => ({
      creditor: String(a.creditor || '').trim(),
      account_type: a.account_type,
      instalment: a.instalment === '' ? null : a.instalment,
      balance: a.balance === '' ? null : a.balance,
      status: a.status,
      report_date: a.report_date || form?.report_date || '',
      source: a.source || form?.source || 'Bureau',
    })),
});

/** The saved report as the form edits it. */
export const toForm = (report) => ({
  report_date: report?.report_date || '',
  bureau_name: report?.bureau_name || '',
  source: report?.source || 'Bureau',
  accounts: (report?.accounts || []).map((a) => ({
    creditor: a.creditor || '',
    account_type: a.account_type || 'Other',
    instalment: a.instalment === null || a.instalment === undefined ? '' : String(a.instalment),
    balance: a.balance === null || a.balance === undefined ? '' : String(a.balance),
    status: a.status || 'Active',
    report_date: a.report_date || '',
    source: a.source || 'Bureau',
  })),
});

/** Started from the client's own report: its date, every row Client-supplied. */
export const fromClientReport = (form, clientReport) => ({
  ...form,
  report_date: clientReport?.report_date || form.report_date,
  source: 'ClientSupplied',
  accounts: (form.accounts || []).map((a) => ({ ...a, source: 'ClientSupplied' })),
});

const amount = (v) => {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * What the affordability check will take from the grid: the monthly
 * instalments of accounts still owed, how many are microloans, and how many
 * are adverse (each a decline).
 */
export const bureauTotals = (accounts) => {
  const owed = (Array.isArray(accounts) ? accounts : []).filter((a) => String(a.creditor || '').trim() && !NOT_OWED.includes(a.status));
  return {
    monthly: Math.round(owed.reduce((sum, a) => sum + amount(a.instalment), 0) * 100) / 100,
    accounts: owed.length,
    microloans: owed.filter((a) => a.account_type === 'Microloan').length,
    adverse: owed.filter((a) => ADVERSE.includes(a.status)).length,
  };
};

/** Days between the report date and today (null without a date). */
export const reportAgeDays = (reportDate, today = new Date()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(reportDate || ''))) return null;
  const d = new Date(`${reportDate}T00:00:00`);
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((t - d) / 86400000);
};
